import cx from 'classnames';
import { CSSProperties, forwardRef, useMemo } from 'react';
import MapGL, {
  FillLayer,
  LineLayer,
  MapboxEvent,
  MapLayerMouseEvent,
  MapRef,
  NavigationControl,
  ViewState,
  ViewStateChangeEvent,
} from 'react-map-gl';

import { useAgencyConstants, useConfig } from '@core/contexts/ConfigContext';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Loading } from '@shared/components/loading';
import { colors } from '@shared/theme/colours';
import { MapPosition } from '@shared/types/common/map';

import { styles } from './MapboxMap.styles';

export const defaultMapboxZoom = 12;
// Fallback when a disposal has no stored map_position_zoom (legacy MapBoxHelper.getDisposalZoom)
export const propertyPinFallbackMapboxZoom = 11;

export const polygonFillLayer: FillLayer = {
  id: 'data',
  type: 'fill',
  paint: {
    'fill-color': colors.blue.dark,
    'fill-outline-color': colors.blue.dark,
    'fill-opacity': 0.1,
  },
};

export const polygonLineLayer: LineLayer = {
  id: 'radius',
  type: 'line',
  paint: {
    'line-color': colors.blue.dark,
    'line-dasharray': [3, 1.5],
    'line-opacity': 0.6,
    'line-width': 2,
  },
};

export interface MapboxMapProps extends WithStyles<typeof styles> {
  cursor?: string;
  disabled?: boolean;
  initialMapPosition?: MapPosition;
  loading?: boolean;
  scrollZoom?: boolean;
  style?: CSSProperties;
  onMapClick?: (e: MapLayerMouseEvent) => void;
  onMapDrag?: (e: ViewStateChangeEvent) => void;
  onMapLoad?: (e: MapboxEvent) => void;
  onMapZoom?: (e: ViewStateChangeEvent) => void;
}

const MapboxMapComponent = forwardRef<MapRef, MapboxMapProps>(
  (
    {
      children,
      classes,
      cursor,
      disabled = false,
      initialMapPosition,
      loading = false,
      scrollZoom = true,
      onMapClick,
      onMapDrag,
      onMapLoad,
      onMapZoom,
      style,
    },
    ref
  ) => {
    const { mapboxAccessToken, mapboxStyles } = useConfig();
    const { constants } = useAgencyConstants();

    const initialViewState: Partial<ViewState> | undefined = useMemo(() => {
      if (loading) {
        return undefined;
      }

      return {
        latitude: initialMapPosition?.lat ?? constants.localisation.lat,
        longitude: initialMapPosition?.lng ?? constants.localisation.lng,
        zoom: initialMapPosition?.zoom ?? defaultMapboxZoom,
        padding: { top: 40, right: 40, bottom: 40, left: 40 },
      };
    }, [constants.localisation, initialMapPosition, loading]);

    const mapStyle = useMemo(() => ({ ...style, width: '100%', height: '100%' }), [style]);

    return (
      <div className={cx(classes.root, { [classes.rootDisabled]: disabled })}>
        {loading && <Loading absolute />}
        {initialViewState && (
          <MapGL
            attributionControl={false}
            cursor={cursor}
            initialViewState={initialViewState}
            logoPosition="bottom-right"
            mapboxAccessToken={mapboxAccessToken}
            mapStyle={`mapbox://styles/${mapboxStyles}`}
            ref={ref}
            scrollZoom={scrollZoom}
            style={mapStyle}
            onClick={onMapClick}
            onLoad={onMapLoad}
            onZoomEnd={onMapZoom}
            onDragEnd={onMapDrag}
          >
            {children}
            {!disabled && <NavigationControl position={'bottom-right'} />}
          </MapGL>
        )}
      </div>
    );
  }
);

export const MapboxMap = withStyles(styles)(MapboxMapComponent);
